import { ExtraCategory } from "../../../domain/ExtraCategory.enum";
import { CategoryLimit, INCLUDED_RANGE, MAX_RANGE } from "../../../domain/entities/CategoryLimit";
import { IssueField, ValidationIssue } from "../../../domain/entities/ValidationIssue";
import { CategoryLimitsLabels } from "../../formatters/CategoryLimitsLabels";

/** Turns an issue into the text shown to the user (`IssueFormatter.format`). */
export type FormatIssue = (issue: ValidationIssue) => string;

/** rjsf error schema of the `categoryLimits` property: `<CATEGORY>.<field>.__errors`. */
export type CategoryLimitsErrorSchema =
  | Partial<Record<string, Partial<Record<string, { __errors?: string[] }>>>>
  | undefined;

/** Messages shown in a block, per control. `block` holds those with no known control. */
export interface CategoryLimitBlockErrors {
  readonly included: readonly string[];
  readonly max: readonly string[];
  readonly extras: readonly string[];
  readonly block: readonly string[];
}

export interface LimitSelectViewModel {
  readonly id: string;
  readonly helpId: string;
  readonly errorId: string;
  /** `""` when empty, so that 0 stays a real value. */
  readonly value: string;
  readonly options: readonly number[];
  readonly errors: readonly string[];
}

export interface ExtraLineViewModel {
  readonly ticker: string;
  readonly id: string;
  readonly name: string;
  readonly unitType: string;
  readonly price: string;
  readonly inactive: boolean;
  readonly checked: boolean;
  /** No inventory line of this accommodation: shown under the line while the block is on. */
  readonly warning?: string;
}

export interface CategoryLimitBlockViewModel {
  readonly category: ExtraCategory;
  readonly baseId: string;
  readonly toggleId: string;
  readonly extrasTitleId: string;
  readonly extrasErrorId: string;
  readonly enabled: boolean;
  readonly categoryPlural: string;
  readonly categorySingular: string;
  /** The [accommodation] of the help texts. */
  readonly accommodation: string;
  readonly included: LimitSelectViewModel;
  readonly max: LimitSelectViewModel;
  readonly lines: readonly ExtraLineViewModel[];
  readonly extrasErrors: readonly string[];
  readonly blockErrors: readonly string[];
  /** The first sellable ticked extra, the one included free of charge; `undefined` hides the text. */
  readonly firstFreeExtra?: string;
}

const BLOCK_FIELDS: readonly string[] = [IssueField.INCLUDED, IssueField.MAX, IssueField.EXTRAS];

const range = ({ min, max }: { min: number; max: number }): number[] =>
  Array.from({ length: max - min + 1 }, (_, index) => min + index);

const INCLUDED_OPTIONS = Object.freeze(range(INCLUDED_RANGE));
const MAX_OPTIONS = Object.freeze(range(MAX_RANGE));

/** A dropdown value back to a count: `""` is empty, not 0. */
export function parseLimitOption(value: string): number | undefined {
  return value === "" ? undefined : Number(value);
}

/**
 * The messages of one block: the client pre-checks that rjsf placed in `errorSchema` and the
 * issues of the last rejected save, each next to its control.
 */
export function collectBlockErrors(
  category: ExtraCategory,
  errorSchema: CategoryLimitsErrorSchema,
  serverIssues: readonly ValidationIssue[],
  format: FormatIssue
): CategoryLimitBlockErrors {
  const blockIssues = serverIssues.filter(issue => issue.targetsBlock(category));
  const forField = (field: IssueField) => [
    ...(errorSchema?.[category]?.[field]?.__errors ?? []),
    ...blockIssues.filter(issue => issue.targetsField(field)).map(format),
  ];
  return {
    included: forField(IssueField.INCLUDED),
    max: forField(IssueField.MAX),
    extras: forField(IssueField.EXTRAS),
    block: blockIssues
      .filter(issue => !BLOCK_FIELDS.includes(issue.target.field ?? ""))
      .map(format),
  };
}

interface BuildBlockViewModelInput {
  readonly block: CategoryLimit;
  readonly errors: CategoryLimitBlockErrors;
  readonly accommodationName: string;
  readonly labels: CategoryLimitsLabels;
  readonly format: FormatIssue;
}

/** Everything a block renders, computed from the block aggregate and the local ticks; the server validates again on save. */
export function buildCategoryLimitBlockViewModel({
  block,
  errors,
  accommodationName,
  labels,
  format,
}: BuildBlockViewModelInput): CategoryLimitBlockViewModel {
  const baseId = `category-limits-${block.category}`;
  const warnings = block.inlineInventoryWarnings();
  const warningOf = (ticker: string) => {
    const warning = warnings.find(issue => issue.target.targetsExtra(ticker));
    return warning && format(warning);
  };
  const select = (
    field: IssueField.INCLUDED | IssueField.MAX,
    value: number | undefined,
    options: readonly number[]
  ): LimitSelectViewModel => {
    const id = `${baseId}-${field}`;
    return {
      id,
      helpId: `${id}-help`,
      errorId: `${id}-error`,
      value: value === undefined ? "" : String(value),
      options,
      errors: errors[field],
    };
  };

  return {
    category: block.category,
    baseId,
    toggleId: `${baseId}-enabled`,
    extrasTitleId: `${baseId}-extras-title`,
    extrasErrorId: `${baseId}-extras-error`,
    enabled: block.enabled,
    categoryPlural: labels.categoryPlural(block.category),
    categorySingular: labels.categorySingular(block.category),
    accommodation: labels.accommodation(accommodationName),
    included: select(IssueField.INCLUDED, block.included, INCLUDED_OPTIONS),
    max: select(IssueField.MAX, block.max, MAX_OPTIONS),
    lines: block.extras.map(line => ({
      ticker: line.ticker,
      id: `${baseId}-extra-${line.ticker}`,
      name: line.name,
      unitType: labels.unitType(line.unitType),
      price: labels.price(line.price),
      inactive: !line.isTickable(),
      checked: block.isTicked(line),
      warning: warningOf(line.ticker),
    })),
    extrasErrors: errors.extras,
    blockErrors: errors.block,
    firstFreeExtra: block.firstFreeExtra()?.name,
  };
}
